import { Component, Input } from "@angular/core";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { Wallet } from "@models/interfaces";

import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { TransactionListDetailComponent } from "../transaction-list-detail.component";
import { AssetTransaction } from "@models/transaction";
import { AccountService } from "@modules/account/services/account.service";
import { FiatService } from "@main-services/currency";
import { ContactService } from "@main-services/contact.service";
import { PayAccountService } from "@main-services/pay_account.service";
import { ModalService } from "@core-services/modal.service";
import { MasterdataService } from "@core-services/masterdata.service";
import { TranslationService } from "@core-services/translation.service";
import { map } from "rxjs/operators";
import { CryptoDepositFacade } from "@modules/+new/payments/features/crypto-deposit/domain/+state/crypto-deposit.facade";
import { CryptoDepositStateService } from "@modules/+new/payments/features/crypto-deposit/domain/+state/crypto-deposit-state.service";
import { Observable } from "rxjs";
import { LaunchDarklyService } from "@core-services/launch-darkly/launch-darkly.service";
import { CryptocoinsState } from "@modules/+new/payments/shared-payments/services/cryptocoins-state.service";

@Component({
	selector: "asset-transaction-list-detail",
	templateUrl: "./asset-transaction-list-detail.component.html",
	providers: [CryptoDepositFacade, CryptoDepositStateService],
	standalone: false
})
export class AssetTransactionListDetailComponent extends TransactionListDetailComponent {
	@Input() public transaction: AssetTransaction = null;
	public networkName = this.getNetworkName();

	public constructor(
		protected modalService: ModalService,
		protected tradableWalletListService: TradableWalletListService,
		protected tradableListService: TradableListService,
		protected accountService: AccountService,
		protected fiatWalletService: FiatWalletService,
		protected fiatService: FiatService,
		protected contactService: ContactService,
		protected payaccountService: PayAccountService,
		protected masterdataService: MasterdataService,
		protected translationService: TranslationService,
		protected depositCryptocoinFacade: CryptoDepositFacade,
		protected cryptocoinsState: CryptocoinsState,
		protected launchDarklyService: LaunchDarklyService
	) {
		super(
			modalService,
			tradableWalletListService,
			tradableListService,
			fiatWalletService,
			fiatService,
			contactService,
			payaccountService,
			masterdataService
		);
	}

	public getFromWallet(): Wallet {
		if (
			this.transaction.getType() === "withdrawal" ||
			(this.transaction.getType() === "transfer" && this.isOutgoing())
		) {
			return this.tradableWallet;
		}

		if (this.isInternal) {
			if (this.transaction.isOutgoing()) {
				return this.tradableWalletListService.getItemById(this.transaction.getWalletId());
			}
			return this.tradableWalletListService.getItemById(this.transaction.getRelatedWalletId());
		}
		return super.getFromWallet();
	}

	public getToWallet(): Wallet {
		if (
			(this.transaction.getType() === "transfer" && !this.isOutgoing()) ||
			this.transaction.getType() === "deposit"
		) {
			return this.tradableWallet;
		}

		if (this.isInternal) {
			if (this.transaction.isOutgoing()) {
				return this.tradableWalletListService.getItemById(this.transaction.getRelatedWalletId());
			}
			return this.tradableWalletListService.getItemById(this.transaction.getWalletId());
		}
		return super.getToWallet();
	}

	public getFromWalletName(): string {
		if (
			this.transaction.getType() === "transfer" &&
			!this.transaction.isOutgoing() &&
			this.getRecipient().length > 0
		) {
			return this.transaction.getFrom();
		}
		if (this.transaction.getType() === "deposit") {
			return this.translationService.translate("external_sender");
		}
		if (this.getGiveawayReward() || this.isOldGiveaway()) {
			return this.translationService.translate("Bitpanda");
		}
		if (this.getRecipient().length === 0 && this.transaction.getFrom().length > 0) {
			return this.transaction.getFrom();
		}
		return super.getFromWalletName();
	}

	public getNetworkName(): Observable<string> {
		return this.cryptocoinsState.cryptocoins.pipe(
			map((list) => list.find((item) => item.cryptocoin_id.toString() === this.transaction.getCryptocoinId())),
			map((crypto) => {
				const network = crypto?.networks.find(
					(network) => network.coin_network_id === this.transaction.getCoinNetworkId()
				);
				return network ? network.name : "";
			})
		);
	}

	public getVsnFeeAmount(): string {
		if (!this.transaction.hasTrade() || !this.transaction.isVsnFeeCollection()) {
			return "0";
		}
		return this.transaction.getTrade().getVsnFeeCollection().attributes.vsn_fee_amount;
	}

	public getVsnFeeRate(): string {
		if (!this.transaction?.hasTrade() || !this.transaction.isVsnFeeCollection()) {
			return "0";
		}

		const trade = this.transaction.getTrade();
		const vsnCollection = trade?.getVsnFeeCollection();
		if (!vsnCollection) {
			return "0";
		}

		return vsnCollection.attributes.vsn_fee_rate || "0";
	}
}
